import { useState } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import { useNavigate } from 'react-router-dom'
import PhoneInput from 'components/Inputs/PhoneInput'
import MaskedInputService from 'services/maskedInput'
import CheckBox from 'components/CheckBox'
import MainButton from 'components/Buttons/MainButton'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import ChangePictureModal from 'pages/Modals/ChangePictureModal'
import AlertDialog from 'components/AlertDialog'
import { isValid as isValidCNPJ } from '@fnando/cnpj'
import editBusinessDataValidator from 'validators/editBusinessDataValidator'
import { FaStar, FaPlus } from 'react-icons/fa'
import { Formik } from 'formik'

import {
  CheckRow,
  Row,
  Form,
  BigImage,
  ImageContainer,
  RatingContainer,
  Label,
  SubTitle,
  LinkText,
  FormField,
  WarningText,
  Input,
  MaskedInput
} from '../../styles'

export default function MainData ({
  business,
  requestConfig,
  onDataUpdated: handleDataUpdated = () => {}
}) {
  const [showOkDialog, setShowOkDialog] = useState (false)
  const [showPictureDialog, setShowPictureDialog] = useState (false)
  const user = AuthService.getUser ()
  const hasEditPermission = AuthService.hasPermission ('business_config.personal_data', 'edit')
  const navigate = useNavigate ()

  const handleChangePicture = newPictureUrl => {
    AuthService.updateUser ({ business: {...user, logo: newPictureUrl }})
    handleDataUpdated ({ logo: newPictureUrl })
  }

  const togglePictureDialog = () => setShowPictureDialog (prev => !prev)
  const toggleOkDialog = () => setShowOkDialog (prev => !prev)

  return (
    <Formik
      validationSchema={editBusinessDataValidator}
      validateOnChange={false}
      validateOnBlur={true}
      initialValues={{
        name: business.name,
        corporate_name: business.corporate_name,
        email: business.email,
        cnpj: business.cnpj,
        use_cnpj: isValidCNPJ (business.cnpj),
        cellphone: business.cellphone,
        phone: business.phone,
        owner: business.owner,
        owner_email: business.owner_email,
        owner_phone: business.owner_phone
      }}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          setSubmitting (true)
          const { use_cnpj, ...dataToUpdate } = values

          await cutileiApi.put (`/businesses/${business.id}`, dataToUpdate, requestConfig)

          handleDataUpdated (dataToUpdate)
          setSubmitting (false)
          toggleOkDialog ()
        } catch (error) {
          setSubmitting (false)
          logApiError (error)
        }
      }}
    >
      {({
        dirty,
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        handleChange,
        handleBlur,
        handleSubmit
      }) => {
        const handleDocumentTypeChange = useCnpj => {
          if (values.use_cnpj === useCnpj) return
          setFieldValue ('cnpj', '')
          setFieldValue ('use_cnpj', !values.use_cnpj)
        }

        return (
          <Form>
            <SubTitle>Dados do salão</SubTitle>
            <ImageContainer onClick={togglePictureDialog} disabled={!hasEditPermission}>
              <BigImage src={business.logo}/>
              {business?.ratings_count > 0 && (
                <RatingContainer>
                  <FaStar size={16} color='#FFC100'/>
                  <SubTitle>
                    {business?.average_rating}
                  </SubTitle>
                </RatingContainer>
              )}
            </ImageContainer>
            {!business?.parent_id && (
              <SecondaryButton
                label='Cadastrar filial'
                leftIcon={<FaPlus size={12} color='#FFC100' style={{marginBottom: 1}}/>}
                onClick={() => navigate ('/signup')}
                width={160}
                style={{marginBottom: 10}}
              />
            )}
            <Row>
              {business?.ratings_count !== 0 && (
                <LinkText to='/business/ratings'>
                  {`${business?.ratings_count} ${business?.ratings_count === 1 ? 'avaliação' : 'avaliações'}`}
                </LinkText>
              )}
            </Row>
            <Row style={{alignItems: 'flex-end'}}>
              <FormField stretch={2}>
                <Input
                  placeholder='Nome fantasia'
                  value={values.name}
                  disabled={!hasEditPermission}
                  onChange={handleChange ('name')}
                  onBlur={handleBlur ('name')}
                />
                {touched.name && errors.name && (
                  <WarningText>{errors.name}</WarningText>
                )}
              </FormField>
              <FormField stretch={2}>
                <Input
                  placeholder='Razão social'
                  value={values.corporate_name}
                  disabled={!hasEditPermission}
                  onChange={handleChange ('corporate_name')}
                  onBlur={handleBlur ('corporate_name')}
                />
                {touched.corporate_name && errors.corporate_name && (
                  <WarningText>{errors.corporate_name}</WarningText>
                )}
              </FormField>
              <FormField stretch={2}>
                {hasEditPermission && (
                  <Row style={{gap: 15, marginBottom: 3}}>
                    <CheckRow>
                      <CheckBox
                        id='use_cnpj'
                        value={values.use_cnpj}
                        onValueChange={() => handleDocumentTypeChange (true)}
                      />
                      <Label htmlFor='use_cnpj'>Usar CNPJ</Label>
                    </CheckRow>
                    <CheckRow>
                      <CheckBox
                        id='use_cpf'
                        value={!values.use_cnpj}
                        onValueChange={() => handleDocumentTypeChange (false)}
                      />
                      <Label htmlFor='use_cpf'>Usar CPF</Label>
                    </CheckRow>
                  </Row>
                )}
                <MaskedInput
                  key={values.use_cnpj ? 'cnpj' : 'cpf'}
                  name='cnpj'
                  placeholder={values.use_cnpj ? 'CNPJ' : 'CPF'}
                  options={{
                    blocks: values.use_cnpj ? [2,3,3,4,2] : [3,3,3,2],
                    delimiters: values.use_cnpj ? ['.', '.','/', '-'] : ['.', '.', '-'],
                    numericOnly: true
                  }}
                  value={values.cnpj}
                  disabled={!hasEditPermission}
                  onChange={event => MaskedInputService.handleChange (event, setFieldValue)}
                  onBlur={handleBlur ('cnpj')}
                />
                {touched.cnpj && errors.cnpj && (
                  <WarningText>{errors.cnpj}</WarningText>
                )}
              </FormField>
            </Row>
            <Row>
              <FormField stretch={2}>
                <Input
                  type='email'
                  placeholder='E-mail'
                  value={values.email}
                  disabled={!hasEditPermission}
                  onChange={handleChange ('email')}
                  onBlur={handleBlur ('email')}
                />
                {touched.email && errors.email && (
                  <WarningText>{errors.email}</WarningText>
                )}
              </FormField>
              <FormField>
                <PhoneInput
                  name='cellphone'
                  placeholder='Celular'
                  value={values.cellphone}
                  disabled={!hasEditPermission}
                  onChange={setFieldValue}
                  onBlur={setFieldTouched}
                />
                {touched.cellphone && errors.cellphone && (
                  <WarningText>{errors.cellphone}</WarningText>
                )}
              </FormField>
              <FormField>
                <PhoneInput
                  name='phone'
                  placeholder='Tel. fixo (opcional)'
                  value={values.phone}
                  disabled={!hasEditPermission}
                  onChange={setFieldValue}
                  onBlur={setFieldTouched}
                />
              </FormField>
            </Row>
            <SubTitle>Dados do responsável</SubTitle>
            <Row style={{marginBottom: 3}}>
              <FormField stretch={6}>
                <Input
                  placeholder='Nome completo do responsável'
                  value={values.owner}
                  disabled={!hasEditPermission}
                  onChange={handleChange ('owner')}
                  onBlur={handleBlur ('owner')}
                />
                {touched.owner && errors.owner && (
                  <WarningText>{errors.owner}</WarningText>
                )}
              </FormField>
              <FormField stretch={6}>
                <Input
                  type='email'
                  placeholder='E-mail do responsável'
                  value={values.owner_email}
                  disabled={!hasEditPermission}
                  onChange={handleChange ('owner_email')}
                  onBlur={handleBlur ('owner_email')}
                />
                {touched.owner_email && errors.owner_email && (
                  <WarningText>{errors.owner_email}</WarningText>
                )}
              </FormField>
              <FormField stretch={5}>
                <PhoneInput
                  name='owner_phone'
                  placeholder='Celular do responsável'
                  value={values.owner_phone}
                  disabled={!hasEditPermission}
                  onChange={setFieldValue}
                  onBlur={setFieldTouched}
                />
                {touched.owner_phone && errors.owner_phone && (
                  <WarningText>{errors.owner_phone}</WarningText>
                )}
              </FormField>
            </Row>
            <ChangePictureModal
              title='Alterar foto de perfil'
              visible={showPictureDialog}
              onConfirm={handleChangePicture}
              onClose={togglePictureDialog}
            />
            <AlertDialog
              visible={showOkDialog}
              title='Sucesso'
              message='Alterações salvas!'
              confirmText='Ok'
              onClose={toggleOkDialog}
            />
            {hasEditPermission && dirty && (
              <MainButton
                label='Salvar alterações'
                onClick={handleSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
                width={160}
                style={{marginTop: 15}}
              />
            )}
          </Form>
        )
      }}
    </Formik>
  )
}
